import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {isMobileAtom} from 'atoms/layout';
import {HomeService} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {format, isWithinInterval} from 'date-fns';
import {useAtom, useAtomValue} from 'jotai';
import {Fragment, ReactNode, useMemo} from 'react';
import {useSearchParams} from 'react-router';
import {getService} from 'utilities';

import styles from './Banner.module.css';
import {bannerDismissedAtom} from './bannerDismissed';
import {BANNER_CONFIGS} from './Banners';

const focusDayStart = new Date(2025, 10, 26);
const focusDayEnd = new Date(2025, 10, 27);

const hackWeekStart = new Date(2025, 6, 21);
const hackWeekEnd = new Date(2025, 6, 26);

const osnameStart = new Date('2026-10-20T15:00:00-04:00');
const osnameEnd = new Date('2026-10-23T15:00:00-04:00');

export const Banner = () => {
  const boxSize = {width: 200, height: 113};
  const isMobile = useAtomValue(isMobileAtom);
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const [dismissed, setDismissed] = useAtom(bannerDismissedAtom);

  const [searchParams] = useSearchParams();

  const forceBanner = searchParams.get('banner');

  const {data} = useQuery({
    queryKey: ['home', 'banner'],
    queryFn: getService(HomeService).bannerApiV1HomeBannerGet,
  });

  const isFocusDay = useMemo(() => {
    return isWithinInterval(new Date(), {start: focusDayStart, end: focusDayEnd});
  }, []);

  const isHackWeek = useMemo(() => {
    return isWithinInterval(new Date(), {start: hackWeekStart, end: hackWeekEnd});
  }, []);

  const isOsnameWindow = useMemo(() => {
    return isWithinInterval(new Date(), {start: osnameStart, end: osnameEnd});
  }, []);

  const fallbackType = isFocusDay ? 'focus' : isHackWeek ? 'hw2025' : undefined;

  // The rename call-out overrides calendar-driven banners (e.g. focus day) while it runs.
  const bannerConfig = {
    ...BANNER_CONFIGS,
  }[
    (forceBanner?.split('-')?.[0] ||
      (isOsnameWindow ? 'osname' : undefined) ||
      data?.company_os_type ||
      fallbackType) as keyof typeof BANNER_CONFIGS
  ];

  if (forceBanner && forceBanner?.split('-')?.[1]) {
    bannerConfig.force = forceBanner?.split('-')?.[1] as 'before' | 'after' | 'live';
  }

  // 9 am PST on 3/25/2025
  const eventDate = new Date('2025-04-22T09:00:00-07:00');

  if (!bannerConfig || (bannerConfig.excludeContractor && employee.type === 'Contractor')) {
    return null;
  }

  // Respect a prior dismissal (per banner type), but always show when a banner
  // is force-previewed via `?banner=` so it stays testable.
  if (!forceBanner && bannerConfig.dismissible && dismissed[bannerConfig.type]) {
    return null;
  }

  return (
    <Box
      borderRadius="Medium"
      borderColor="Text Base"
      borderStyle="Solid"
      marginBottom="32"
      marginTop="16"
      position="relative"
      // Banner text is always dark, so gradients that fade out must land on white in dark mode too.
      style={{background: `${bannerConfig.background}, #FFFFFF`, minHeight: 124}}
    >
      {bannerConfig.type !== 'soapbox' && (
        <Box
          paddingY="6"
          backgroundColor="Background Base"
          borderColor="Text Base"
          borderBottom="Solid"
          style={{borderTopLeftRadius: 8, borderTopRightRadius: 8}}
        >
          <Marquee text={bannerConfig.getMarquee(eventDate)} eventStart={eventDate} />
        </Box>
      )}

      <Box
        style={{
          marginLeft: isMobile ? 16 : boxSize.width + 56,
          marginTop:
            (isMobile ? boxSize.height / 2 + 24 : 0) + (bannerConfig.type === 'soapbox' ? 12 : 0),
        }}
        paddingY="12"
        display="flex"
        flexDirection="column"
      >
        <ThemeProvider mode="bright">
          <ThemeContainer>
            <Text className={styles.brandSharp}>{bannerConfig.title}</Text>
            <Text>{bannerConfig.getSubtitle(eventDate)}</Text>
          </ThemeContainer>
        </ThemeProvider>
      </Box>

      <Video
        background={bannerConfig.imageBackground}
        image={bannerConfig.image}
        imageNode={bannerConfig.imageNode}
        {...boxSize}
      />

      {!forceBanner && bannerConfig.dismissible && (
        <Box className={styles.closeButton}>
          <IconButton
            size="small"
            variant="filled"
            aria-label="Dismiss banner"
            onClick={() => {
              analyticsLogger().logEvent('HOME_BANNER_CLICKED', {
                action: 'dismiss',
                type: bannerConfig.type,
              });
              setDismissed((prev) => ({...prev, [bannerConfig.type]: true}));
            }}
          >
            <UIIcon src={CloseLine} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const Video = ({
  background,
  image,
  imageNode,
  floatingImage,
  circular,
  ...boxSize
}: {
  image?: string;
  imageNode?: ReactNode;
  background?: string;
  floatingImage?: boolean;
  circular?: boolean;
  width: number;
  height: number;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  return (
    <Box position="absolute" top="0" left="0">
      <Box position="relative" style={boxSize}>
        <Box
          position="absolute"
          backgroundColor="Background Base"
          style={{...boxSize, left: 36, bottom: 1}}
          borderRadius={circular ? 'Circular' : 'XLarge'}
          borderColor="Text Base"
          borderStyle="Solid"
        />
        <Box
          position="absolute"
          backgroundColor="Background Base"
          style={{...boxSize, left: 24, bottom: 9}}
          borderRadius={circular ? 'Circular' : 'XLarge'}
          borderColor="Text Base"
          borderStyle="Solid"
          bottom="0"
          right="0"
        />
        {circular ? (
          <>
            <Box
              position="absolute"
              style={{...boxSize, left: 12, bottom: 17, background}}
              borderRadius={circular ? 'Circular' : 'XLarge'}
              borderColor="Text Base"
              borderStyle="Solid"
              bottom="0"
              right="0"
            />
            <Box
              position="absolute"
              style={{left: 23, top: -6, transform: 'scale(1.16)', objectFit: 'cover'}}
              borderRadius="Circular"
              overflow="hidden"
            >
              <Avatar key="april-fool" size="profile" user={employee} />
            </Box>
          </>
        ) : image && !floatingImage ? (
          <img
            style={{
              ...boxSize,
              left: 12,
              bottom: 17,
              background,
              // objectFit: 'cover',
            }}
            src={image}
            className={atoms({
              position: 'absolute',
              backgroundColor: 'Background Base',
              borderRadius: circular ? 'Circular' : 'XLarge',
              borderColor: 'Text Base',
              borderStyle: 'Solid',
              bottom: '0',
              right: '0',
            })}
          />
        ) : (
          <>
            <Box
              position="absolute"
              style={{...boxSize, left: 12, bottom: 17, background}}
              borderRadius={circular ? 'Circular' : 'XLarge'}
              borderColor="Text Base"
              borderStyle="Solid"
              bottom="0"
              right="0"
            />
            {imageNode && (
              <Box
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{...boxSize, left: 12, bottom: 17}}
              >
                {imageNode}
              </Box>
            )}
            {image && (
              <img
                style={{left: 24, bottom: 10, transform: 'scale(1.06)', objectFit: 'cover'}}
                src={image}
                className={atoms({
                  position: 'absolute',
                  borderRadius: circular ? 'Circular' : 'XLarge',
                })}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const Marquee = ({text, eventStart}: {text: string; eventStart: Date}) => {
  const date = format(eventStart, 'EEE, MMM d');

  const time = format(eventStart, 'h:mma').toLowerCase();

  const timezone =
    Intl.DateTimeFormat(undefined, {timeZoneName: 'short'})
      .formatToParts(eventStart)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return (
    <div className={styles.marquee}>
      <Text className={styles.marqueeSlider} isBold size="xsmall">
        <Box
          display="inline-flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          style={{gap: 12, textTransform: 'uppercase'}}
        >
          {Array.from({length: 15}).map((_, i) => (
            <Fragment key={i}>
              <span>{text === '$DATE' ? (i % 2 === 0 ? date : `${time} ${timezone}`) : text}</span>
              <span>—</span>
            </Fragment>
          ))}
        </Box>
        <Box
          display="inline-flex"
          flexDirection="row"
          paddingX="12"
          alignItems="center"
          justifyContent="center"
          style={{gap: 12, textTransform: 'uppercase'}}
        >
          {Array.from({length: 15}).map((_, i) => (
            <Fragment key={i}>
              <span>{text === '$DATE' ? (i % 2 === 0 ? date : `${time} ${timezone}`) : text}</span>
              <span>—</span>
            </Fragment>
          ))}
        </Box>
      </Text>
    </div>
  );
};
